<script setup>
import {
  DatePicker,
  InputMask,
  InputNumber,
  InputText,
  Rating,
  Select,
  Textarea,
} from "primevue";
import { computed } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  placeholder: {
    type: String,
    default: "",
  },
  iconClickable: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxlength: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: () => `input-${Math.random().toString(36).substr(2, 9)}`,
  },
  icon: {
    type: String,
    default: "",
  },
  autocomplete: {
    type: String,
    default: "off",
  },
  inputClass: {
    type: [String, Object, Array],
    default: "",
  },
  isDark: {
    type: Boolean,
    default: true,
  },
  wrapperClass: {
    type: String,
    default: "",
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: null,
  },
  mask: {
    type: String,
    default: "999-999",
  },
  options: {
    type: Array,
    default: () => [],
  },
  optionLabel: {
    type: String,
    default: "",
  },
  default: {
    type: [String, Number],
    default: "",
  },
  modelValue: {
    type: [String, Number, Object, Date],
    default: null,
  },
  showMarker: {
    type: Boolean,
    default: false,
  },
  onSelectChange: {
    type: Function,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  ratingSize: {
    type: Number,
    default: 20,
  },
});

const emit = defineEmits(["update:modelValue", "onIconClick"]);

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const handleIconClick = () => {
  if (props.iconClickable && !props.disabled) {
    emit("onIconClick");
  }
};
</script>

<template>
  <div v-if="type === 'select'" class="mt-25" :class="wrapperClass">
    <span v-if="label" class="custom-label d-flex ml-5 mb-5 font-weight-600">
      {{ label }}
    </span>
    <Select
      v-model="inputValue"
      :options="options"
      :optionLabel="optionLabel"
      show-clear
      :placeholder="placeholder"
      fluid
      v-on:change="onSelectChange"
    >
      <template #option="slotprops">
        <div class="d-flex flex-1 align-items-center justify-between">
          <span>{{ slotprops.option.label }}</span>
          <span
            class="marker bg-primary p-5 br-50 h-max"
            v-if="slotprops.option.alreadySet"
          />
        </div>
      </template>
    </Select>
  </div>
  <div v-else-if="type === 'calendar'" class="mt-25" :class="wrapperClass">
    <span v-if="label" class="custom-label d-flex ml-5 mb-5 font-weight-600">
      {{ label }}
    </span>
    <DatePicker
      :placeholder="placeholder"
      v-model="inputValue"
      fluid
      show-icon
      show-other-months
      select-other-months
      :min-date="new Date()"
      auto-z-index
      date-format="yy-mm-dd"
    />
  </div>

  <div
    v-else-if="type === 'rating'"
    class="mt-25 d-flex flex-direction-column"
    :class="wrapperClass"
  >
    <span v-if="label" class="custom-label d-flex ml-5 mb-5 font-weight-600">
      {{ label }}
    </span>

    <Rating
      v-model="inputValue"
      :default-value="default"
      :readonly="readOnly"
      :class="[inputClass]"
      :disabled="disabled"
      :pt="{
        onIcon: {
          style: {
            width: `${ratingSize}px`,
            height: `${ratingSize}px`,
            color: 'var(--an-rating)',
          },
        },
        offIcon: {
          style: {
            width: `${ratingSize}px`,
            height: `${ratingSize}px`,
            color: 'var(--an-gray)',
          },
        },
        root: {
          class: 'gap-10 flex-1 ph-10',
        },
      }"
    />
  </div>

  <div v-else class="mt-25">
    <label
      v-if="label"
      :for="id"
      class="custom-label d-flex ml-5 mb-5 font-weight-600"
    >
      {{ label }}
    </label>

    <div
      class="input-wrapper d-flex align-items-center position-relative"
      :class="[isDark ? 'dark-wrapper' : 'light-wrapper', wrapperClass]"
    >
      <template v-if="type === 'phone'">
        <InputMask
          v-model="inputValue"
          :id="id"
          fluid
          :mask="mask"
          :placeholder="placeholder"
          :disabled="disabled"
          :required="required"
          :autocomplete="autocomplete"
          :pt="{
            pcInputText: {
              class: ['custom-input', isDark ? 'dark-input' : 'light-input'],
            },
          }"
        />
      </template>
      <template v-else-if="type === 'textarea'">
        <Textarea
          :id="id"
          rows="3"
          fluid
          :auto-resize="true"
          :placeholder="placeholder"
          :class="[
            'custom-input',
            inputClass,
            isDark ? 'dark-input' : 'light-input',
          ]"
          :required="required"
        />
      </template>
      <template v-else-if="type === 'currency'">
        <InputNumber
          :input-id="id"
          v-model="inputValue"
          :placeholder="placeholder"
          mode="currency"
          currency="INR"
          currencyDisplay="symbol"
          locale="en-IN"
          :min-fraction-digits="0"
          fluid
          :class="[
            'custom-input',
            inputClass,
            isDark ? 'dark-input' : 'light-input',
          ]"
          :disabled="disabled"
        />
      </template>
      <template v-else>
        <InputText
          :id="id"
          :readonly="readOnly"
          fluid
          :type="type"
          v-model="inputValue"
          :default-value="default"
          :placeholder="placeholder"
          :disabled="disabled"
          :required="required"
          :autocomplete="autocomplete"
          :class="[
            'custom-input',
            inputClass,
            isDark ? 'dark-input' : 'light-input',
          ]"
          :maxlength="maxlength"
        />
      </template>
      <div
        v-if="icon"
        class="input-icon font-20"
        @click="handleIconClick"
        :class="isDark ? 'dark-input' : 'light-input'"
      >
        <i :class="icon"></i>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-wrapper {
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.dark-wrapper {
  border: 1px solid var(--an-gray2);
}

.light-wrapper {
  border: 1px solid var(--an-gray);
}

.dark-input {
  color: var(--an-white);
}

.light-input {
  color: var(--an-text-primary);
}

.dark-wrapper:focus-within {
  border: 1px solid var(--an-white);
}

.light-wrapper:focus-within {
  border: 1px solid var(--an-text-primary);
}

.custom-input {
  flex: 1;
  width: 100%;
  padding: 15px;
  border: none;
  outline: none;
  background: transparent;
}

.custom-input:disabled {
  background-color: var(--an-lightgray);
  cursor: not-allowed;
}

.input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 0.75rem;
}
</style>
